import {
  addDoc,
  collection,
  doc,
  increment,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  setDoc,
  deleteField,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { uploadProofImage } from './uploadProof';
import { normalizeOrderEvent } from './events';


function stripUndefined(value) {
  if (Array.isArray(value)) return value.map(stripUndefined).filter((item) => item !== undefined);
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return Object.entries(value).reduce((acc, [key, item]) => {
      if (item !== undefined) acc[key] = stripUndefined(item);
      return acc;
    }, {});
  }
  return value;
}

async function addEvent(orgId, orderId, event) {
  if (!orgId || !orderId) return;
  const normalized = normalizeOrderEvent(event || {});
  const clientAt = new Date().toISOString();
  const eventPayload = stripUndefined({
    ...normalized,
    clientAt,
    createdAt: serverTimestamp(),
  });
  await addDoc(collection(db, 'orgs', orgId, 'ontimeOrders', orderId, 'events'), eventPayload);

  // Embedded timeline keeps the order card useful even before the live events listener loads.
  const timelineRow = stripUndefined({
    type: normalized.type,
    legacyType: normalized.legacyType || '',
    title: normalized.title,
    text: normalized.message || normalized.title,
    actorLabel: normalized.actorLabel || '',
    at: clientAt,
    meta: normalized.meta || {},
    photoUrl: normalized.photoUrl || '',
  });
  await updateDoc(doc(db, 'orgs', orgId, 'ontimeOrders', orderId), {
    timeline: arrayUnion(timelineRow),
    lastTimelineEvent: timelineRow,
    updatedAt: serverTimestamp(),
  });
}

async function patchOrder(orgId, orderId, patch) {
  const ref = doc(db, 'orgs', orgId, 'ontimeOrders', orderId);
  await updateDoc(ref, {
    updatedAt: serverTimestamp(),
    ...patch,
  });
}

async function setDriverState(orgId, driverId, patch) {
  if (!orgId || !driverId) return;
  const driverRef = doc(db, 'orgs', orgId, 'ontimeDrivers', driverId);
  await setDoc(
    driverRef,
    {
      updatedAt: serverTimestamp(),
      ...patch,
    },
    { merge: true }
  );
}

export async function logIntakeCreatedEvent({ orgId, orderId, actor, message, patch = {} }) {
  await patchOrder(orgId, orderId, {
    ...patch,
    'timings.createdAt': patch?.timings?.createdAt || serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'intake_created',
    actor,
    message: message || 'Order created from intake',
  });
}

export async function updateOrderStage({ orgId, orderId, stage, actor, patch = {}, message }) {
  await patchOrder(orgId, orderId, {
    stage,
    ...patch,
  });
  await addEvent(orgId, orderId, {
    type: 'status_change',
    to: stage,
    actor,
    message: message || `Moved order to ${stage}`,
  });
}

export async function flagOrderIssue({ orgId, orderId, actor, note }) {
  await patchOrder(orgId, orderId, {
    stage: 'issue',
    issueFlag: true,
    issueType: 'general_issue',
    issueNote: note || 'Issue flagged from Link',
    'flags.hasStockIssue': true,
    'notes.online': note || 'Issue flagged from Link',
  });
  await addEvent(orgId, orderId, {
    type: 'issue_flagged',
    actor,
    message: note || 'Issue flagged',
  });
}

export async function assignDriver({ orgId, orderId, driverId, driverName, actor }) {
  await patchOrder(orgId, orderId, {
    assignedDriver: driverName || driverId,
    assignedDriverName: driverName || driverId,
    'assignment.driverId': driverId || '',
    'assignment.driverName': driverName || '',
    stage: 'ready_for_driver',
    'timings.driverAssignedAt': serverTimestamp(),
  });
  await setDriverState(orgId, driverId, {
    activeOrderId: orderId,
    currentOrderId: orderId,
    status: 'out_for_delivery',
    activeLoad: increment(1),
  });
  await addEvent(orgId, orderId, {
    type: 'driver_assigned',
    actor,
    message: `Assigned driver ${driverName || driverId}`,
  });
}


export async function storeAcceptOrder({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'awaiting_store',
    storeStatus: 'accepted',
    'fulfilment.storeStatus': 'accepted',
    'timings.storeAcceptedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'store_accepted',
    actor,
    message: 'Store accepted the order',
  });
}

export async function storeReadyForDriverWithPackedProof({ file, orgId, orderId, actor }) {
  let photoUrl = '';
  if (file) {
    photoUrl = await uploadProofImage({ file, orgId, orderId });
  }

  const patch = {
    stage: 'ready_for_driver',
    storeStatus: 'ready_for_driver',
    'fulfilment.storeStatus': 'ready_for_driver',
    'timings.packedAt': serverTimestamp(),
    'timings.readyForDriverAt': serverTimestamp(),
  };

  if (photoUrl) {
    patch['storeProof.packedPhotoUrl'] = photoUrl;
    patch['storeProof.packedAt'] = serverTimestamp();
    patch['storeProof.packedBy'] = actor?.name || actor?.email || '';
    patch['storeProof.packedById'] = actor?.userId || '';
    patch['packerName'] = actor?.name || actor?.email || '';
    patch['assignedStoreUserName'] = actor?.name || actor?.email || '';
    patch['proof.packedPhotoUrl'] = photoUrl;
  }

  await patchOrder(orgId, orderId, patch);
  await addEvent(orgId, orderId, {
    type: 'store_ready_for_driver',
    actor,
    message: photoUrl ? 'Store marked order ready for driver with packed proof' : 'Store marked order ready for driver',
    photoUrl,
  });
}

export async function storeConfirmAll({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'awaiting_store',
    'flags.hasStockIssue': false,
    issueFlag: false,
    'timings.storeConfirmedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'store_confirmed',
    actor,
    message: 'Store confirmed all items',
  });
}

export async function storeSuggestSwap({ orgId, orderId, actor, note }) {
  await patchOrder(orgId, orderId, {
    stage: 'issue',
    issueFlag: true,
    issueType: 'swap_requested',
    issueNote: note || 'Store suggested a swap',
    'flags.hasStockIssue': true,
    'timings.swapApprovalStartedAt': serverTimestamp(),
    'notes.store': note || 'Store suggested a swap',
  });
  await addEvent(orgId, orderId, {
    type: 'store_swap_requested',
    actor,
    message: note || 'Store suggested a swap',
  });
}

export async function storeMarkPacking({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'awaiting_store',
    storeStatus: 'packing',
    'fulfilment.storeStatus': 'packing',
    'notes.store': 'Packing in progress',
    'packerName': actor?.name || actor?.email || '',
    'assignedStoreUserName': actor?.name || actor?.email || '',
    'timings.storeAcceptedAt': serverTimestamp(),
    'timings.packingStartedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'packing_started',
    actor,
    message: 'Store marked order as packing',
  });
}

export async function storeStartPacking(args) {
  return storeMarkPacking(args);
}

export async function storeReadyForDriver({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'ready_for_driver',
    storeStatus: 'ready_for_driver',
    'fulfilment.storeStatus': 'ready_for_driver',
    'timings.packedAt': serverTimestamp(),
    'timings.readyForDriverAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'ready_for_driver',
    actor,
    message: 'Store marked order ready for driver',
  });
}

export async function storePackedWithProof({ file, orgId, orderId, actor }) {
  if (!file) throw new Error('Please take or select a packed-order photo first.');
  const photoUrl = await uploadProofImage({ file, orgId, orderId });
  await patchOrder(orgId, orderId, {
    stage: 'ready_for_driver',
    storeStatus: 'ready_for_driver',
    'fulfilment.storeStatus': 'ready_for_driver',
    'timings.packedAt': serverTimestamp(),
    'timings.readyForDriverAt': serverTimestamp(),
    'storeProof.packedPhotoUrl': photoUrl,
    'storeProof.packedAt': serverTimestamp(),
    'storeProof.packedBy': actor?.name || actor?.email || '',
    'storeProof.packedById': actor?.userId || '',
    'packerName': actor?.name || actor?.email || '',
    'assignedStoreUserName': actor?.name || actor?.email || '',
    'proof.packedPhotoUrl': photoUrl,
  });
  await addEvent(orgId, orderId, {
    type: 'store_packed_proof',
    actor,
    message: 'Store captured packed proof',
    photoUrl,
  });
}

export async function storeMarkCollected({ file, orgId, orderId, actor, deliveryMode = 'link' }) {
  let photoUrl = '';
  if (file) {
    photoUrl = await uploadProofImage({ file, orgId, orderId, kind: 'collected' });
  }
  const mode = String(deliveryMode || 'link').toLowerCase().includes('other') ? 'other' : 'link';
  const patch = {
    stage: 'ready_for_driver',
    storeStatus: 'collected',
    deliveryMode: mode,
    handoffMode: mode,
    'assignment.deliveryMode': mode,
    'assignment.handoffMode': mode,
    'fulfilment.storeStatus': 'collected',
    'assignment.siteHandedToDriver': true,
    'timings.collectedAt': serverTimestamp(),
  };
  if (photoUrl) {
    patch['proof.collectedPhotoUrl'] = photoUrl;
    patch['storeProof.collectedPhotoUrl'] = photoUrl;
    patch['storeProof.collectedAt'] = serverTimestamp();
    patch['storeProof.collectedBy'] = actor?.name || actor?.email || '';
    patch['storeProof.collectedById'] = actor?.userId || '';
  }
  await patchOrder(orgId, orderId, patch);
  await addEvent(orgId, orderId, {
    type: 'store_collected_by_driver',
    actor,
    message: photoUrl ? 'Site marked order collected with handover proof' : 'Site marked order collected by driver',
    photoUrl,
  });
}


export async function storeCompleteOtherDriver({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'delivered',
    storeStatus: 'completed',
    deliveryMode: 'other',
    handoffMode: 'other',
    'assignment.deliveryMode': 'other',
    'assignment.handoffMode': 'other',
    'assignment.otherDriverCompletedBySite': true,
    'timings.deliveredAt': serverTimestamp(),
    'timings.completedAt': serverTimestamp(),
    'timings.driverDeliveredAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'other_driver_completed_by_site',
    actor,
    message: 'Site completed delivery handled by other driver',
  });
}

export async function storeMoveBackToWaiting({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'awaiting_store',
    storeStatus: 'waiting',
    'fulfilment.storeStatus': 'waiting',
    'timings.packingStartedAt': deleteField(),
    'timings.packedAt': deleteField(),
    'timings.readyForDriverAt': deleteField(),
    'proof.packedPhotoUrl': deleteField(),
  });
  await addEvent(orgId, orderId, {
    type: 'site_moved_back_to_waiting',
    actor,
    message: 'Site moved order back to Waiting',
  });
}

function resolveProductLinePrice(product = {}, qty = 1) {
  const safeQty = Math.max(0.5, Number(qty) || 1);
  const directUnit = Number(
    product?.price ??
    product?.sellPrice ??
    product?.unitPrice ??
    product?.amount ??
    product?.sellingPrice ??
    product?.retailPrice ??
    product?.priceEach ??
    product?.pricing?.price ??
    product?.pricing?.unitPrice ??
    0
  ) || 0;
  if (directUnit > 0) return { unitPrice: directUnit, lineTotal: directUnit * safeQty };

  const rules = Array.isArray(product?.priceRules) ? product.priceRules : Array.isArray(product?.pricing?.priceRules) ? product.pricing.priceRules : [];
  const exact = rules.find((rule) => Math.abs((Number(rule?.qty ?? rule?.quantity) || 0) - safeQty) < 0.0001);
  if (exact && Number(exact.price ?? exact.amount) > 0) return { unitPrice: Number(exact.price ?? exact.amount) / safeQty, lineTotal: Number(exact.price ?? exact.amount) };

  const baseQty = Math.max(0.0001, Number(product?.baseQty ?? product?.qty ?? product?.quantity ?? product?.pricing?.baseQty ?? 1) || 1);
  const basePrice = Number(product?.basePrice ?? product?.baseAmount ?? product?.pricing?.basePrice ?? product?.pricing?.baseAmount ?? 0) || 0;
  if (basePrice > 0) {
    const unit = basePrice / baseQty;
    return { unitPrice: unit, lineTotal: unit * safeQty };
  }

  return { unitPrice: 0, lineTotal: 0 };
}

function actionItemName(item = {}) {
  return String(item.name || item.productName || item.title || item.sku || 'Item').trim();
}

function actionItemQty(item = {}) {
  return Number(item.qty ?? item.quantity ?? item.count ?? 1) || 1;
}

function actionItemPrice(item = {}) {
  const direct = Number(item.lineTotal ?? item.total ?? item.amount ?? 0);
  if (direct) return direct;
  const unit = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.value ?? 0) || 0;
  return unit * actionItemQty(item);
}

function actionIsAdditionItem(item = {}) {
  const id = String(item.id || item.lineId || item.productId || '').toLowerCase();
  const status = String(item.status || item.itemStatus || item.type || '').toLowerCase();
  return item.isSwap === true || item.isAddition === true || item.addedBySite || item.addedAtLabel || item.swapFor || id.startsWith('swap_') || status.includes('addition') || status.includes('swap');
}

export async function storeAddSwapProduct({ orgId, orderId, order = {}, product, qty = 1, replacedItemName = '', lineTotalOverride, actor }) {
  const name = String(product?.name || product?.productName || product?.title || 'Swap item').trim();
  const safeQty = Math.max(0.5, Number(qty) || 1);
  const resolved = resolveProductLinePrice(product, safeQty);
  const customLineTotal = Number(lineTotalOverride);
  const lineTotal = Number.isFinite(customLineTotal) && customLineTotal >= 0 ? customLineTotal : resolved.lineTotal;
  const price = safeQty > 0 ? lineTotal / safeQty : resolved.unitPrice;
  const swapItem = {
    id: `swap_${Date.now()}_${String(product?.id || name).replace(/\W+/g, '_')}`,
    productId: product?.id || '',
    name,
    qty: safeQty,
    quantity: safeQty,
    price,
    unitPrice: price,
    lineTotal,
    isSwap: true,
    swapFor: replacedItemName || '',
    addedBySite: actor?.name || '',
    addedAtLabel: new Date().toISOString(),
  };
  const currentItems = Array.isArray(order?.originalOrderItems) && order.originalOrderItems.length
    ? order.originalOrderItems
    : Array.isArray(order?.raw?.originalOrderItems) && order.raw.originalOrderItems.length
      ? order.raw.originalOrderItems
      : Array.isArray(order?.raw?.items) ? order.raw.items : Array.isArray(order?.items) ? order.items : [];
  const originalItems = currentItems.filter((item) => !actionIsAdditionItem(item));
  const delivery = Number(order?.raw?.pricing?.deliveryFee ?? order?.pricing?.deliveryFee ?? order?.deliveryFee ?? order?.delivery ?? 0) || 0;
  const originalSubtotal = originalItems.reduce((sum, item) => sum + actionItemPrice(item), 0);
  const existingOriginalTotal = Number(order?.raw?.originalPaidTotal ?? order?.raw?.originalOrderTotal ?? order?.raw?.pricing?.originalTotal ?? order?.raw?.pricing?.total ?? order?.originalPaidTotal ?? order?.originalOrderTotal ?? order?.total ?? 0);
  const fixedOriginalTotal = Number.isFinite(existingOriginalTotal) && existingOriginalTotal > 0 ? existingOriginalTotal : originalSubtotal + delivery;

  await patchOrder(orgId, orderId, {
    additions: arrayUnion(swapItem),
    originalOrderItems: originalItems,
    originalPaidTotal: fixedOriginalTotal,
    issueFlag: true,
    issueType: 'swap_selected',
    'flags.hasStockIssue': true,
    'timings.swapApprovalStartedAt': serverTimestamp(),
    'notes.store': replacedItemName ? `Swap selected for ${replacedItemName}: ${name}` : `Swap selected: ${name}`,
  });
  await addEvent(orgId, orderId, {
    type: 'site_swap_product_added',
    actor,
    productName: name,
    replacedItemName,
    message: replacedItemName ? `Site added swap ${name} for ${replacedItemName}` : `Site added swap ${name}`,
  });
}


export async function storeRemoveSwapProduct({ orgId, orderId, order, item, actor }) {
  if (!item) return;
  const targetId = String(item?.id || item?.productId || item?.name || item?.productName || item?.title || '');
  const currentItems = Array.isArray(order?.items) ? order.items : [];
  const currentAdditions = Array.isArray(order?.additions) ? order.additions : [];
  const nextItems = currentItems.filter((row) => String(row?.id || row?.productId || row?.name || row?.productName || row?.title || '') !== targetId);
  const nextAdditions = currentAdditions.filter((row) => String(row?.id || row?.productId || row?.name || row?.productName || row?.title || '') !== targetId);
  await patchOrder(orgId, orderId, {
    items: nextItems,
    additions: nextAdditions,
    issueFlag: true,
    issueType: 'swap_removed',
    'flags.hasStockIssue': true,
    'timings.swapApprovalStartedAt': serverTimestamp(),
    'notes.store': `Swap removed: ${item?.name || item?.productName || item?.title || item?.id || 'Swap item'}`,
  });
  await addEvent(orgId, orderId, {
    type: 'site_swap_product_removed',
    actor,
    productName: item?.name || item?.productName || item?.title || '',
    message: `Site removed swap ${item?.name || item?.productName || item?.title || item?.id || 'item'}`,
  });
}

export async function driverRequestRun({ orgId, orderId, driverId, driverName, actor }) {
  if (!orgId || !orderId || !driverId) return;
  await patchOrder(orgId, orderId, {
    driverRequestIds: arrayUnion(driverId),
    driverRequests: arrayUnion({ driverId, driverName: driverName || actor?.name || driverId }),
    'timings.driverRequestedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'driver_requested_run',
    actor,
    driverId,
    driverName: driverName || actor?.name || '',
    message: `${driverName || actor?.name || 'Driver'} accepted the ready run and is waiting for site selection`,
  });
}

export async function siteSelectDriver({ orgId, orderId, driverId, driverName, actor }) {
  if (!orgId || !orderId || !driverId) return;
  await patchOrder(orgId, orderId, {
    assignedDriver: driverName || driverId,
    assignedDriverName: driverName || driverId,
    'assignment.driverId': driverId || '',
    'assignment.driverName': driverName || '',
    'assignment.siteSelectedDriver': true,
    'assignment.selectedBySiteAt': serverTimestamp(),
    'timings.driverAssignedAt': serverTimestamp(),
  });
  await setDriverState(orgId, driverId, {
    activeOrderId: orderId,
    currentOrderId: orderId,
    currentOrderRef: orderId,
    driverName: driverName || '',
    status: 'selected',
  });
  await addEvent(orgId, orderId, {
    type: 'site_selected_driver',
    actor,
    driverId,
    driverName: driverName || '',
    message: `Site selected driver ${driverName || driverId}`,
  });
}


export async function siteClearSelectedDriver({ orgId, orderId, actor }) {
  if (!orgId || !orderId) return;
  await patchOrder(orgId, orderId, {
    assignedDriver: deleteField(),
    assignedDriverName: deleteField(),
    'assignment.driverId': deleteField(),
    'assignment.driverName': deleteField(),
    'assignment.siteSelectedDriver': false,
    'assignment.selectedBySiteAt': deleteField(),
    'timings.driverAssignedAt': deleteField(),
  });
  await addEvent(orgId, orderId, {
    type: 'site_unselected_driver',
    actor,
    message: 'Site unselected the Link driver',
  });
}

export async function driverStartRun({ file, orgId, orderId, driverId, driverName, actor, location = null }) {
  if (!file) throw new Error('Please take a photo of the order before starting the run.');
  let photoUrl = '';
  if (file) photoUrl = await uploadProofImage({ file, orgId, orderId });
  const patch = {
    stage: 'out_for_delivery',
    currentOwner: 'driver',
    assignedDriver: driverName || actor?.name || driverId || '',
    assignedDriverName: driverName || actor?.name || '',
    'assignment.driverId': driverId || '',
    'assignment.driverName': driverName || actor?.name || '',
    'timings.driverAcceptedAt': serverTimestamp(),
    'timings.outForDeliveryAt': serverTimestamp(),
  };
  if (photoUrl) {
    patch.driverProof = {
      ...(location ? { acceptLocation: location } : {}),
      startedPhotoUrl: photoUrl,
      acceptedPhotoUrl: photoUrl,
      acceptedAt: serverTimestamp(),
      acceptedBy: driverName || actor?.name || actor?.email || '',
      acceptedById: driverId || '',
    };
    patch['proof.driverStartedPhotoUrl'] = photoUrl;
    patch['proof.driverAcceptedPhotoUrl'] = photoUrl;
    patch['proof.driverPickupPhotoUrl'] = photoUrl;
    patch['driverProof.pickupPhotoUrl'] = photoUrl;
    patch['driverProof.pickupAt'] = serverTimestamp();
  }
  if (location) {
    patch.currentDriverLocation = {
      ...location,
      driverId: driverId || '',
      driverName: driverName || actor?.name || '',
      updatedAt: serverTimestamp(),
    };
  }

  await patchOrder(orgId, orderId, patch);
  await setDriverState(orgId, driverId, {
    activeOrderId: orderId,
    currentOrderId: orderId,
    currentOrderRef: orderId,
    driverName: driverName || actor?.name || '',
    status: 'out_for_delivery',
    ...(location ? { location: { ...location, updatedAt: serverTimestamp() } } : {}),
  });
  await addEvent(orgId, orderId, {
    type: 'driver_start_run',
    actor,
    photoUrl,
    location: location || null,
    message: photoUrl ? 'Driver started the run with order photo' : 'Driver started the run',
  });
}


export async function driverAcceptRun(args = {}) {
  // Backwards-compatible export for older Link driver/admin screens.
  // New Driver PWA uses driverRequestRun -> siteSelectDriver -> driverStartRun.
  return driverStartRun(args);
}


export async function setDriverAvailability({ orgId, driverId, driverName = '', site = '', status = 'offline', location = null }) {
  if (!orgId || !driverId) return;
  const online = status === 'online';
  await setDriverState(orgId, driverId, {
    driverName,
    site,
    status: online ? 'online' : 'offline',
    isOnline: online,
    availability: online ? 'online' : 'offline',
    lastSeenAt: serverTimestamp(),
    ...(online ? { onlineAt: serverTimestamp() } : { offlineAt: serverTimestamp() }),
    ...(location ? { location: { ...location, updatedAt: serverTimestamp() } } : {}),
  });
}

export async function updateDriverLocation({ orgId, driverId, driverName = '', orderId = '', location }) {
  if (!orgId || !driverId || !location) return;
  await setDriverState(orgId, driverId, {
    driverName,
    status: 'online',
    location: {
      ...location,
      updatedAt: serverTimestamp(),
    },
    lastSeenAt: serverTimestamp(),
  });
  if (orderId) {
    await patchOrder(orgId, orderId, {
      currentDriverLocation: {
        ...location,
        driverId,
        driverName,
        updatedAt: serverTimestamp(),
      },
    });
  }
}

export async function driverPickedUp({ orgId, orderId, driverId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'ready_for_driver',
    'timings.pickedUpAt': serverTimestamp(),
  });
  await setDriverState(orgId, driverId, {
    activeOrderId: orderId,
    currentOrderId: orderId,
    status: 'picked_up',
  });
  await addEvent(orgId, orderId, {
    type: 'picked_up',
    actor,
    message: 'Driver picked up the order',
  });
}

export async function driverOnRoute({ orgId, orderId, driverId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'out_for_delivery',
    'timings.outForDeliveryAt': serverTimestamp(),
  });
  await setDriverState(orgId, driverId, {
    activeOrderId: orderId,
    currentOrderId: orderId,
    status: 'delivering',
  });
  await addEvent(orgId, orderId, {
    type: 'out_for_delivery',
    actor,
    message: 'Driver is on route to the customer',
  });
}

export async function markDeliveredWithProof({
  file,
  orgId,
  orderId,
  driverId,
  driverName,
  note = '',
  receiverName = '',
  signatureData = '',
  actor,
}) {
  if (!file) throw new Error('Please select a proof image.');
  if (!orgId || !orderId) throw new Error('Missing orgId or orderId.');

  const photoUrl = await uploadProofImage({ file, orgId, orderId });

  await patchOrder(orgId, orderId, {
    stage: 'delivered',
    currentOwner: 'completed',
    issueFlag: false,
    'proof.photoUrl': photoUrl,
    'proof.deliveredPhotoUrl': photoUrl,
    'proof.deliveredAt': serverTimestamp(),
    'proof.deliveredBy': driverName || actor?.name || actor?.email || '',
    'proof.deliveredById': driverId || '',
    'proof.receiverName': receiverName,
    'proof.note': note,
    'proof.signatureData': signatureData,
    'driverProof.deliveredPhotoUrl': photoUrl,
    'driverProof.deliveredAt': serverTimestamp(),
    'driverProof.deliveredBy': driverName || actor?.name || actor?.email || '',
    'driverProof.deliveredById': driverId || '',
    'timings.deliveredAt': serverTimestamp(),
    'timings.driverDeliveredAt': serverTimestamp(),
    'timings.completedAt': serverTimestamp(),
  });

  if (driverId) {
    await setDriverState(orgId, driverId, {
      activeOrderId: '',
      currentOrderId: '',
      status: 'available',
      activeLoad: 0,
    });
  }

  await addEvent(orgId, orderId, {
    type: 'delivered_with_proof',
    actor,
    message: 'Driver delivered the order with proof',
    photoUrl,
    note,
    receiverName,
    signatureCaptured: Boolean(signatureData),
  });
}

export async function driverDelivered({ orgId, orderId, driverId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'delivered',
    issueFlag: false,
    'timings.deliveredAt': serverTimestamp(),
  });
  await setDriverState(orgId, driverId, {
    activeOrderId: '',
    currentOrderId: '',
    status: 'available',
  });
  await addEvent(orgId, orderId, {
    type: 'delivered',
    actor,
    message: 'Driver delivered the order',
  });
}

export async function storeCancelOrder({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    stage: 'cancelled',
    storeStatus: 'cancelled',
    'fulfilment.storeStatus': 'cancelled',
    'timings.cancelledAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'site_cancelled_order',
    actor,
    message: 'Site cancelled the order from Link Site PWA',
  });
}

export async function storeRequestCancelApproval({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    issueFlag: true,
    cancelApprovalRequested: true,
    cancellationRequested: true,
    cancelRequestStatus: 'pending_control_tower',
    'cancelRequest.requestedAt': serverTimestamp(),
    'cancelRequest.status': 'pending_control_tower',
    'cancelRequest.requestedBy': actor?.name || actor?.email || 'Site app',
    'cancelRequest.requestedByRole': actor?.role || 'site',
  });
  await addEvent(orgId, orderId, {
    type: 'site_cancel_requested',
    actor,
    message: 'Site requested cancellation approval from Control Tower',
  });
}



export async function storeWithdrawCancelApproval({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    issueFlag: false,
    cancelApprovalRequested: false,
    cancellationRequested: false,
    cancelRequestStatus: 'withdrawn',
    'cancelRequest.status': 'withdrawn',
    'cancelRequest.withdrawnAt': serverTimestamp(),
    'cancelRequest.withdrawnBy': actor?.name || actor?.email || 'Site app',
  });
  await addEvent(orgId, orderId, {
    type: 'site_cancel_request_withdrawn',
    actor,
    message: 'Site withdrew the cancellation approval request',
  });
}

function actionLineKey(item = {}, idx = 0) {
  const name = String(item?.name || item?.productName || item?.title || item?.sku || 'Item').trim();
  const qty = actionItemQty(item);
  const price = actionItemPrice(item);
  return String(item?.__lineKey || item?.lineKey || item?.cartLineId || item?.lineId || item?.orderLineId || item?.rowId || item?.uid || `line_${idx}_${name}_${qty}_${price}`);
}

function sameLineItem(row = {}, idx = 0, itemKey = '') {
  return actionLineKey(row, idx) === String(itemKey || '');
}

function withItemAvailability(order = {}, itemKey = '', itemName = '', unavailable = true) {
  const sourceRows = Array.isArray(order?.originalOrderItems) && order.originalOrderItems.length
    ? order.originalOrderItems
    : Array.isArray(order?.items) ? order.items.filter((item) => !actionIsAdditionItem(item)) : [];
  const nextOriginal = sourceRows.map((row, idx) => {
    if (!sameLineItem(row, idx, itemKey)) return row;
    return {
      ...row,
      __lineKey: actionLineKey(row, idx),
      unavailable,
      unavailableBySite: unavailable,
      unavailableAtLabel: unavailable ? new Date().toISOString() : '',
    };
  });
  const nextItems = Array.isArray(order?.items) ? order.items.map((row, idx) => {
    if (actionIsAdditionItem(row) || !sameLineItem(row, idx, itemKey)) return row;
    return { ...row, __lineKey: actionLineKey(row, idx), unavailable, unavailableBySite: unavailable, unavailableAtLabel: unavailable ? new Date().toISOString() : '' };
  }) : nextOriginal;
  return { nextItems, nextOriginal };
}

export async function storeMarkItemUnavailable({ orgId, orderId, order, itemKey, itemName, actor }) {
  const { nextItems, nextOriginal } = withItemAvailability(order, itemKey, itemName, true);
  const currentMap = (order?.flags && typeof order.flags.unavailableMap === 'object') ? order.flags.unavailableMap : {};
  const nextMap = {
    ...currentMap,
    [String(itemKey || '')]: true,
  };
  await patchOrder(orgId, orderId, {
    stage: 'awaiting_store',
    storeStatus: 'waiting',
    'fulfilment.storeStatus': 'waiting',
    issueFlag: true,
    issueType: 'item_unavailable',
    issueNote: `${itemName || itemKey} unavailable`,
    'flags.hasStockIssue': true,
    'flags.unavailableMap': nextMap,
    items: nextItems,
    originalOrderItems: nextOriginal,
    unavailableItems: arrayUnion(String(itemKey || '')),
    unavailableItemNames: arrayRemove(String(itemName || itemKey || '')),
    'timings.unavailableMarkedAt': serverTimestamp(),
    'timings.swapApprovalStartedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'site_item_unavailable',
    actor,
    itemKey,
    itemName,
    message: `${itemName || itemKey} marked unavailable`,
  });
}

export async function storeReactivateItem({ orgId, orderId, order, itemKey, itemName, actor }) {
  const { nextItems, nextOriginal } = withItemAvailability(order, itemKey, itemName, false);
  const currentMap = (order?.flags && typeof order.flags.unavailableMap === 'object') ? order.flags.unavailableMap : {};
  const nextMap = { ...currentMap };
  delete nextMap[String(itemKey || '')];
  await patchOrder(orgId, orderId, {
    items: nextItems,
    originalOrderItems: nextOriginal,
    'flags.unavailableMap': nextMap,
    unavailableItems: arrayRemove(String(itemKey || '')),
    unavailableItemNames: arrayRemove(String(itemName || itemKey || ''), String(itemKey || itemName || '')),
  });
  await addEvent(orgId, orderId, {
    type: 'site_item_reactivated',
    actor,
    itemKey,
    itemName,
    message: `${itemName || itemKey} reactivated`,
  });
}

export async function linkOrderSendMessage({ orgId, orderId, actor, text, file = null, media = null }) {
  const cleanText = String(text || '').trim();
  let uploadedUrl = '';
  if (file) uploadedUrl = await uploadProofImage({ file, orgId, orderId });
  const mediaPayload = media && typeof media === 'object' ? media : null;
  if (!cleanText && !uploadedUrl && !mediaPayload) throw new Error('Type a message or attach something first.');
  const message = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    text: cleanText,
    attachmentUrl: uploadedUrl,
    attachmentName: file?.name || '',
    attachmentType: uploadedUrl ? String(file?.type || 'image') : '',
    media: mediaPayload,
    actorName: actor?.name || actor?.email || 'Link user',
    actorRole: actor?.role || 'site',
    actorStore: actor?.store || '',
    actorId: actor?.userId || actor?.id || '',
    createdAt: new Date().toISOString(),
  };
  await patchOrder(orgId, orderId, {
    linkChatMessages: arrayUnion(message),
    linkChatLastMessage: cleanText || (uploadedUrl ? 'Photo attached' : mediaPayload?.label || mediaPayload?.type || 'Media'),
    linkChatLastAt: message.createdAt,
    linkChatLastBy: message.actorName,
  });
  await addEvent(orgId, orderId, {
    type: 'link_chat_message',
    actor,
    message: cleanText || (uploadedUrl ? 'Photo attached' : mediaPayload?.label || mediaPayload?.type || 'Media'),
    chatMessageId: message.id,
  });
}

export async function controlTowerApproveAdjustment({ orgId, orderId, actor }) {
  await patchOrder(orgId, orderId, {
    swapApproved: true,
    issueFlag: false,
    issueType: 'adjustment_approved',
    'flags.hasStockIssue': false,
    'timings.swapApprovedAt': serverTimestamp(),
    'notes.online': 'Adjustment approved by Control Tower',
  });
  await addEvent(orgId, orderId, {
    type: 'control_tower_adjustment_approved',
    actor,
    message: 'Control Tower approved the replacement/removal adjustment',
  });
}

export async function controlTowerSendToDifferentStore({ orgId, orderId, order = {}, targetStore, reason = '', actor }) {
  if (!targetStore) throw new Error('Choose a target store first.');
  if (!String(reason || '').trim()) throw new Error('Please capture a reroute reason first.');
  const source = order?.raw || order || {};
  const cleanSource = stripUndefined({ ...source });
  delete cleanSource.id;
  delete cleanSource.archived;
  delete cleanSource.archivedAt;
  delete cleanSource.archivedByName;
  delete cleanSource.archivedByEmail;
  delete cleanSource.archiveCategory;
  delete cleanSource.archiveStore;
  delete cleanSource.archivedDateKey;
  delete cleanSource.reroutedToOrderId;
  const reroutedOrder = stripUndefined({
    ...cleanSource,
    parentOrderId: orderId,
    reroutedFromOrderId: orderId,
    reroutedFromStore: order?.store || source.storeName || source.store || '',
    store: targetStore,
    storeName: targetStore,
    stage: 'awaiting_store',
    storeStatus: 'waiting',
    fulfilment: {
      ...(source.fulfilment || {}),
      storeStatus: 'waiting',
    },
    assignment: {
      ...(source.assignment || {}),
      driverId: '',
      driverName: '',
    },
    assignedDriver: '',
    assignedDriverName: '',
    linkChatMessages: [],
    timeline: [],
    events: [],
    issueFlag: false,
    issueType: '',
    swapApproved: false,
    overallStartedAt: source.overallStartedAt || source.originalCreatedAt || source.createdAt || order.createdAt || null,
    originalCreatedAt: source.originalCreatedAt || source.createdAt || order.createdAt || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    timings: {
      ...(source.timings || {}),
      reroutedAt: serverTimestamp(),
      sentToStoreAt: serverTimestamp(),
    },
  });
  const newRef = await addDoc(collection(db, 'orgs', orgId, 'ontimeOrders'), reroutedOrder);
  await patchOrder(orgId, orderId, {
    stage: 'archived',
    status: 'archived',
    storeStatus: 'archived',
    archived: true,
    archiveVisibleToAdminOnly: true,
    archivedReason: `Rerouted to ${targetStore}: ${String(reason || '').trim()}`,
    archivedByName: actor?.name || actor?.email || 'Control Tower',
    archivedByEmail: actor?.email || '',
    archiveCategory: 'rerouted',
    archiveStore: order?.store || source.storeName || source.store || '',
    archivedDateKey: new Date().toISOString().slice(0, 10),
    rerouteReason: String(reason || '').trim(),
    reroutedToOrderId: newRef.id,
    reroutedToStore: targetStore,
    'timings.archivedAt': serverTimestamp(),
  });
  await addEvent(orgId, orderId, {
    type: 'control_tower_rerouted_order',
    actor,
    message: `Control Tower sent order to ${targetStore}. Original order archived for audit. Reason: ${String(reason || '').trim()}`,
    reason: String(reason || '').trim(),
    targetStore,
    newOrderId: newRef.id,
  });
  await addEvent(orgId, newRef.id, {
    type: 'control_tower_reroute_created',
    actor,
    message: `Clean-slate order created for ${targetStore}`,
    sourceOrderId: orderId,
  });
  return newRef.id;
}


export async function controlTowerCancelOrder({ orgId, orderId, order = {}, reason = '', actor }) {
  if (!String(reason || '').trim()) throw new Error('Please capture a cancellation reason first.');
  const source = order?.raw || order || {};
  const assignment = order?.assignment || source.assignment || {};
  const assignedDriverId = String(assignment.driverId || assignment.selectedDriverId || source.driverId || order?.driverId || '').trim();
  const cleanReason = String(reason || '').trim();

  await patchOrder(orgId, orderId, {
    // Use one terminal state everywhere. Store, Driver, Control Tower and Archives
    // all understand cancelled; archived remains true only for audit visibility.
    stage: 'cancelled',
    status: 'cancelled',
    storeStatus: 'cancelled',
    driverStatus: 'cancelled',
    cancelled: true,
    delivered: false,
    archived: true,
    archiveVisibleToAdminOnly: true,
    archiveCategory: 'cancelled',
    archiveStore: order?.store || source.storeName || source.store || '',
    archivedDateKey: new Date().toISOString().slice(0, 10),
    archivedReason: `Cancelled: ${cleanReason}`,
    cancelReason: cleanReason,
    cancelledReason: cleanReason,
    archivedByName: actor?.name || actor?.email || 'Control Tower',
    archivedByEmail: actor?.email || '',

    // Clear every assignment mirror so cancelled runs instantly disappear from
    // the driver home card, ready-runs list and My Run page.
    assignedDriver: deleteField(),
    assignedDriverName: deleteField(),
    driverId: deleteField(),
    driverName: deleteField(),
    selectedDriver: deleteField(),
    selectedDriverName: deleteField(),
    'assignment.driverId': deleteField(),
    'assignment.driverName': deleteField(),
    'assignment.selectedDriverId': deleteField(),
    'assignment.selectedDriverName': deleteField(),
    'assignment.siteSelectedDriver': false,
    'assignment.selectedBySiteAt': deleteField(),
    'assignment.currentOrderId': deleteField(),
    'assignment.currentOrderRef': deleteField(),
    'timings.driverAssignedAt': deleteField(),
    'timings.archivedAt': serverTimestamp(),
    'timings.cancelledAt': serverTimestamp(),
  });

  if (assignedDriverId) {
    await setDriverState(orgId, assignedDriverId, {
      activeOrderId: deleteField(),
      currentOrderId: deleteField(),
      currentOrderRef: deleteField(),
      status: 'online',
    });
  }

  await addEvent(orgId, orderId, {
    type: 'control_tower_cancelled_order',
    actor,
    message: `Control Tower cancelled the order. Reason: ${cleanReason}`,
    reason: cleanReason,
  });
}
